/**
 * AutismPatientOverview.js
 *
 * Provider-facing Patient Overview for Adult Autism patients. Uses the same
 * layout, components, and visual structure as the ADHD PatientOverview page,
 * but reads from ModularPatientDataContext which fetches from the pathway-
 * namespaced subcollection structure:
 *   patients/{uid}/adult_autism/assessment_001/formInstances/{formKey}
 *   patients/{uid}/adult_autism/assessment_001/assessmentStages/{stageKey}
 *
 * Assessment module order (default launch layout):
 *   1. Diagnostic Developmental History (History + SSI in one appointment)
 *   2. Observation — placeholder until observation proforma ships
 *   3. Diagnostic Decision
 *   4. Post-Assessment Discussion
 *
 * Appointment dates (when wf.isAppointmentDatesEnabled): every card shows the
 * clinician-set date or an estimate inferred from the stage's reportContent
 * timestamps, and the shared AppointmentDateDialog is mounted with the Autism
 * re-finalisation strategy. See the "Per-module appointment dates" block below.
 */
import React, { useEffect, useContext, useState, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../../../../components/Layout';
import { InformationCircleIcon, ChevronRightIcon } from '@heroicons/react/solid';
import { ModularPatientDataContext } from '../../../../context/ModularPatientDataContext';
import PatientInfoPanel from '../components/PatientInfoPanel';
import Breadcrumb from '../../../../components/Breadcrumb';
import StatusMessage from '../../../../components/StatusMessage';
import AssessmentModuleCard from '../components/AssessmentModuleCard';
import AutismPreAppointmentDashboard from './AutismPreAppointmentDashboard';
import useAnalytics from '../../../../hooks/useAnalytics';
import { useAuth } from '../../../../hooks/useAuth';
import useWorkflowConfig from '../../../../hooks/useWorkflowConfig';
import { deriveAdultAutismPatientOverviewConfig } from '../config/patientOverviewConfig';
import { getAssessmentModuleCardWidthClass } from '../utils/getAssessmentModuleCardWidthClass';
import { isSlamOrg } from '../../2_dashboard/config/patientSetupConfig';
import AssignCliniciansDialog from '../components/AssignCliniciansDialog';
import { buildModularPatientOverviewModel, buildFormDataMapFromInstances } from '../utils/buildModularPatientOverviewModel';
import { resolveFormInstanceForDisplay } from '../../../../utils/resolveFormTemplates';
import { fetchEnrichedInformantData } from '../../../../utils/informantFirestorePaths';
import { computeHistoryProgress } from '../../../providers/6_clinicalInterview/autism/AutismHistoryModule';
import {
  computeDevelopmentalDiagnosticInterviewProgress,
  getDevelopmentalDiagnosticInterviewPaths,
} from './developmentalDiagnosticInterviewUtils';
import { appendPreviewQuery, canStartAppointment } from '../../../../utils/appointmentStageRouting';
import { computeSSIProgress } from '../../../../utils/autismSSIProgress';
import { shouldLockModule, getLockedTooltip } from './autismModuleGating';
import {
  getAutismDDStartRoute,
  getAutismDDStepRoutes,
  getCombinedEmergingDifferentials,
  computeAutismDDProgress,
} from '../../../../utils/autismDDRouting';
import {
  getAutismPADStartRoute,
  getAutismPADStepRoutes,
  getAutismDiagnosisOutcome,
  computeAutismPADProgress,
} from '../../../../utils/autismPADRouting';
import {
  getAutismOBSStartRoute,
  getAutismOBSStepRoutes,
  computeAutismOBSProgress,
} from '../../../../utils/autismObsRouting';
import {
  getReportApprovedField,
  getReportGeneratedField,
  getEnabledAutismStages,
  getAutismAppointmentShortKey,
  OVERVIEW_MODULE_TO_STAGE_KEYS,
  STAGE_TO_REPORT_PREFIX,
} from '../../../../constants/adultAutismStages';
import {
  getAppointmentChecklistActionState,
} from '../../../../components/autismModular/appointmentChecklistContent';
import AppointmentDateDialog from '../components/AppointmentDateDialog';
import {
  resolveDisplayAppointmentDate,
  resolveAutismReportDateAnchor,
  toLondonCalendarDateString,
} from '../../../../utils/appointmentDateHelpers';
import {
  predictAutismAppointmentDateReportRefinalisation,
  rerenderAfterAutismAppointmentDateChange,
} from '../../../../utils/rerenderAfterAutismAppointmentDateChange';

function AutismPatientOverview() {
  const { patientId } = useParams();

  const {
    header,
    formInstances,
    assessmentStages,
    reportContent,
    fetchPatientData,
  } = useContext(ModularPatientDataContext);

  const patientDataModel = useMemo(
    () => buildModularPatientOverviewModel({ header, formInstances, assessmentStages }),
    [header, formInstances, assessmentStages],
  );

  const [PAProgress, setPAProgress] = useState(0);
  const [SSIProgress, setSSIProgress] = useState(0);
  const [behaviouralObservationProgress, setBehaviouralObservationProgress] = useState(0);
  const [PADProgress, setPADProgress] = useState(0);
  const [DDProgress, setDDProgress] = useState(0);
  const [informantData, setInformantData] = useState({});
  const { trackEvent } = useAnalytics();
  const { claims, user } = useAuth();
  const wf = useWorkflowConfig(patientDataModel, claims);

  const autismSubcollectionAccess = useMemo(() => ({
    organisation: patientDataModel?.organisation || claims?.organisation || '',
    clinicianUid: user?.uid || '',
    isOrgWideAccess: claims?.userType === 'Org. Admin' || claims?.userType === 'Support',
  }), [patientDataModel?.organisation, claims?.organisation, claims?.userType, user?.uid]);

  const [requestReassignStep, setRequestReassignStep] = useState(null);
  const [showAssignLeadDialog, setShowAssignLeadDialog] = useState(false);

  const organisationName = claims?.organisation || 'default';
  const config = deriveAdultAutismPatientOverviewConfig(wf);
  const isSubclinicalService = organisationName === 'ThinkDivergent';

  // Gate appointment checklist via the stamped workflow config (three-tier
  // resolution in useWorkflowConfig: stamp → legacy org-name → disabled).
  // This ensures Support staff still see it on TOC patients, and LMP patients
  // never do — without ad-hoc org-name checks at the page level.
  const checklistEnabled = wf.isAppointmentChecklistEnabled;

  // ── Per-module appointment dates (parity with the ADHD overview) ───────────
  // Storage is pathway-agnostic: `patients/{id}.appointmentDates.{shortKey}.date`
  // plus the appointmentDateHistory audit subcollection. The adapter spreads the
  // header into patientDataModel, so `patientDataModel.appointmentDates` is the
  // live map. Autism short keys are the dashboard-pill keys (ddi/obs/dd/pad).
  //
  // The INFERRED date (shown as "estimated" when no clinician-entered date
  // exists) comes from two sources, in order:
  //
  //   1. `firstCompletedAt` on the stage's assessmentStages/{stage} doc — the
  //      write-once stamp the proforma writes the first time the clinician
  //      presses Finish. This is the same anchor the ADHD overview uses, so the
  //      two pathways now estimate the same way.
  //   2. The stage's reportContent audit-timestamp chain
  //      (resolveAutismReportDateAnchor — ReportFirstGeneratedAt and friends).
  //      This is the legacy fallback for patients whose stages were completed
  //      before firstCompletedAt existed on Autism; they carry lastCompletedAt
  //      and the report timestamps but no firstCompletedAt.
  //
  // The Autism PDF pipeline (asd_car/render.py::resolve_assessment_date and
  // asd_report/template_data.py) resolves in the same order — manual date,
  // then firstCompletedAt, then the reportContent chain — so the estimate on a
  // card is always the date the CAR / Patient Report would print. The two
  // sources are NOT interchangeable: Autism pages trigger paragraph generation
  // on every section exit, so ReportFirstGeneratedAt is usually days before
  // the Finish press for a stage done over several sittings. Keep the order.
  //
  // The Diagnostic Developmental Interview card covers TWO stages (History and
  // SSI, done in one sitting). For the card's single estimate we take the first
  // stage in OVERVIEW_MODULE_TO_STAGE_KEYS that has an anchor — History first,
  // SSI as fallback — resolving BOTH sources for a stage before moving to the
  // next. Promoting that estimate via "Use estimated date" writes
  // `appointmentDates.ddi`, after which BOTH stages' reports print that date.
  const [appointmentDateDialogModule, setAppointmentDateDialogModule] = useState(null);
  const resolveModuleAppointmentDate = useCallback((moduleKey) => {
    const shortKey = getAutismAppointmentShortKey(moduleKey);
    const stageKeys = OVERVIEW_MODULE_TO_STAGE_KEYS[moduleKey] || [];

    // Convert each candidate through toLondonCalendarDateString and pick the
    // first that yields a valid calendar day. This protects against corrupt
    // Firestore maps from the stripUndefined bug: a truthy-but-unconvertible
    // value (e.g. { _methodName: 'serverTimestamp' }) returns null from the
    // converter rather than short-circuiting the fallback chain. For the DDI
    // module (History + SSI in one sitting), we exhaust ALL candidates for a
    // stage before moving to the next, matching the backend tier order.
    //
    // Candidate order per stage (mirrors resolve_assessment_date in Python):
    //   1. firstCompletedAt — write-once stamp from the Finish press
    //   2. reportContent chain — legacy fallback for pre-firstCompletedAt data
    //
    // lastCompletedAt is deliberately excluded: it moves on every re-finish,
    // so using it would silently change the displayed estimate after a clinician
    // re-opens and re-finishes a stage. The report chain is the stable anchor
    // for legacy patients (same date the PDF prints). See the existing test
    // "falls back to the reportContent chain for a legacy stage".
    let inferredCalendarDay = null;
    for (const stageKey of stageKeys) {
      const stageDoc = assessmentStages?.[stageKey];
      inferredCalendarDay = toLondonCalendarDateString(stageDoc?.firstCompletedAt)
        || toLondonCalendarDateString(
          resolveAutismReportDateAnchor(
            reportContent?.[stageKey],
            STAGE_TO_REPORT_PREFIX[stageKey],
          ),
        );
      if (inferredCalendarDay) break;
    }

    return {
      shortKey,
      ...resolveDisplayAppointmentDate({
        appointmentDate: patientDataModel?.appointmentDates?.[shortKey]?.date,
        // Pass the pre-converted calendar day. resolveDisplayAppointmentDate
        // runs toLondonCalendarDateString again (no-op for a YYYY-MM-DD string)
        // so the isInferred flag is set correctly.
        firstCompletedAt: inferredCalendarDay,
      }),
    };
  }, [assessmentStages, reportContent, patientDataModel?.appointmentDates]);

  // Stages active for this patient's workflow (Observation drops out for LMP).
  // Shared by the dialog's re-finalisation prediction so an LMP patient is
  // never told an Observation CAR will be re-rendered.
  const enabledStageKeys = useMemo(() => getEnabledAutismStages(wf), [wf]);

  // Autism re-finalisation strategy injected into the shared dialog. The
  // prediction reads the live modular context (approval flags on reportContent,
  // PR anchor on the header); the rerender helper re-reads Firestore itself.
  const predictAutismRefinalisation = useCallback(
    (moduleKeys) => predictAutismAppointmentDateReportRefinalisation({
      reportContent,
      header,
      moduleKeys,
      enabledStageKeys,
    }),
    [reportContent, header, enabledStageKeys],
  );

  useEffect(() => {
    if (patientId) {
      trackEvent('autism_patient_overview_viewed', {
        patientId,
        timestamp: new Date().toISOString(),
      });
    }
  }, [patientId, trackEvent]);

  useEffect(() => {
    let isCurrent = true;
    setInformantData({});

    const fetchInformantData = async () => {
      if (!patientDataModel?.informants || Object.keys(patientDataModel.informants).length === 0) {
        return;
      }

      const nextInformantsData = {};
      const informantIds = Object.keys(patientDataModel.informants);

      for (const informantId of informantIds) {
        try {
          const mapEntry = patientDataModel.informants[informantId];
          const enriched = await fetchEnrichedInformantData(
            informantId,
            patientId,
            mapEntry,
            autismSubcollectionAccess,
          );
          if (!isCurrent) return;
          if (enriched) {
            const displayName = mapEntry?.informantName
              || [enriched.firstName, enriched.lastName].filter(Boolean).join(' ')
              || enriched.email
              || 'Informant';

            const patientFirstName = patientDataModel?.firstName || '';
            const resolvedFormInstances = enriched.isAutismInformant
              ? Object.fromEntries(
                Object.entries(enriched.formInstances || {}).map(([key, fi]) => [
                  key,
                  resolveFormInstanceForDisplay(fi, patientFirstName),
                ]),
              )
              : enriched.formInstances;

            nextInformantsData[informantId] = {
              ...enriched,
              displayName,
              formData: enriched.isAutismInformant
                ? buildFormDataMapFromInstances(resolvedFormInstances)
                : enriched.formData,
            };
          }
        } catch (error) {
          console.error(`Error fetching informant data for ${informantId}:`, error);
        }
      }

      if (isCurrent) {
        setInformantData(nextInformantsData);
      }
    };

    fetchInformantData();
    return () => {
      isCurrent = false;
    };
  }, [patientId, patientDataModel?.informants, patientDataModel?.firstName, autismSubcollectionAccess]);

  const shouldDisableModule = (moduleKey) =>
    moduleKey !== 'developmentalDiagnosticInterview'
    && moduleKey !== 'behaviouralObservation'
    && moduleKey !== 'diagnosticDecision'
    && moduleKey !== 'postAssessmentDiscussion';

  const getDisabledReason = (moduleKey) => (
    shouldDisableModule(moduleKey) ? 'In development' : ''
  );

  // Gating context passed to the extracted utility. The util reads modular
  // reportContent approval flags (not the ADHD monolithic dataModel) and
  // assessmentStages completion state — the correct data sources for Autism.
  const gatingContext = useMemo(() => ({
    reportContent,
    assessmentStages,
    wf,
  }), [reportContent, assessmentStages, wf]);

  const isModuleLocked = useCallback(
    (moduleKey) => shouldLockModule({ moduleKey, ...gatingContext }),
    [gatingContext],
  );

  const getModuleLockedTooltip = useCallback(
    (moduleKey) => getLockedTooltip({ moduleKey, ...gatingContext }),
    [gatingContext],
  );

  useEffect(() => {
    if (!patientDataModel) return;

    // ── Shared guard ────────────────────────────────────────────────────
    // A module's progress pill should only reflect data completeness once
    // the assigned clinician has actually opened the module (status
    // promoted from 'notStarted' → 'inProgress' by the first interaction).
    // Before that, prefilled demo data or vacuously-complete steps must
    // not inflate the pill — the clinician hasn't started the work yet.
    const isStarted = (status) => status === 'inProgress' || status === 'completed';

    // History progress: computed from assessmentStages/history prefill coverage
    // + Risk Assessment validity. The second arg passes riskAssessment data so
    // the denominator includes Risk Assessment and it counts as complete when
    // the clinician has entered any substantive input. When the workflow excludes
    // Risk Assessment (ciFlow.riskAssessment: 'disabled', e.g. TOC autism), pass
    // null so the denominator drops the +1 and the pill can reach 100%.
    const historyStage = assessmentStages?.history;
    let historyProgressValue;
    if (historyStage?.status === 'completed') {
      historyProgressValue = 100;
    } else if (!isStarted(historyStage?.status)) {
      historyProgressValue = 0;
    } else {
      historyProgressValue = Math.round(
        computeHistoryProgress(
          historyStage?.data,
          wf.isCIRiskAssessmentEnabled ? historyStage?.riskAssessment : null,
        ) * 100,
      );
    }
    setPAProgress(historyProgressValue);

    const ssiStageData = assessmentStages?.semiStructuredInterview;
    if (!isStarted(ssiStageData?.status)) {
      setSSIProgress(0);
    } else {
      setSSIProgress(computeSSIProgress(ssiStageData));
    }

    // OBS progress: prefer the persisted `progress` fraction, falling back to
    // a fresh compute from the stage data so the pill is correct even before the
    // first incremental write lands (same pattern as DD/PAD).
    const observationStage = assessmentStages?.observation;
    let observationProgressValue;
    if (observationStage?.status === 'completed') {
      observationProgressValue = 100;
    } else if (!isStarted(observationStage?.status)) {
      observationProgressValue = 0;
    } else if (typeof observationStage?.progress === 'number') {
      observationProgressValue = Math.round(observationStage.progress * 100);
    } else {
      observationProgressValue = Math.round(
        computeAutismOBSProgress({
          data: observationStage?.data,
          review: observationStage?.review,
          status: observationStage?.status,
        }) * 100,
      );
    }
    setBehaviouralObservationProgress(observationProgressValue);

    // DD progress now comes from the modular assessmentStages doc (not the
    // legacy monolithic dataModel). A completed stage shows 100; otherwise we
    // prefer the persisted `progress` fraction written by the proforma pages,
    // falling back to a fresh compute from the stage data + differentials union
    // so the pill is correct even before the first incremental write lands.
    const ddStage = assessmentStages?.diagnosticDecision;
    let ddProgressValue;
    if (ddStage?.status === 'completed') {
      ddProgressValue = 100;
    } else if (!isStarted(ddStage?.status)) {
      ddProgressValue = 0;
    } else if (typeof ddStage?.progress === 'number') {
      ddProgressValue = Math.round(ddStage.progress * 100);
    } else {
      const ddUnion = getCombinedEmergingDifferentials(assessmentStages);
      ddProgressValue = Math.round(
        computeAutismDDProgress({ data: ddStage?.data, union: ddUnion, status: ddStage?.status, skipDifferentialsDecisions: wf.skipAutismDifferentialsDecisions }) * 100,
      );
    }
    setDDProgress(ddProgressValue);

    // PAD progress now comes from the modular assessmentStages doc (not the
    // legacy monolithic dataModel). A completed stage shows 100; otherwise we
    // prefer the persisted `progress` fraction written by the proforma pages,
    // falling back to a fresh compute from the stage data + live DD outcome so
    // the pill is correct even before the first incremental write lands.
    const padStage = assessmentStages?.postAssessmentDiscussion;
    let padProgressValue;
    if (padStage?.status === 'completed') {
      padProgressValue = 100;
    } else if (!isStarted(padStage?.status)) {
      padProgressValue = 0;
    } else if (typeof padStage?.progress === 'number') {
      padProgressValue = Math.round(padStage.progress * 100);
    } else {
      padProgressValue = Math.round(
        computeAutismPADProgress({
          data: padStage?.data,
          outcome: getAutismDiagnosisOutcome(assessmentStages),
          status: padStage?.status,
        }) * 100,
      );
    }
    setPADProgress(padProgressValue);
  }, [patientDataModel, assessmentStages, wf.isCIRiskAssessmentEnabled]);

  const ddiProgress = useMemo(() => (
    computeDevelopmentalDiagnosticInterviewProgress({
      historyProgress: PAProgress,
      ssiProgress: SSIProgress,
      isSSIFirst: config.modules.developmentalDiagnosticInterview?.isSSIFirst,
    })
  ), [PAProgress, SSIProgress, config.modules.developmentalDiagnosticInterview?.isSSIFirst]);

  const renderModuleCard = (moduleKey, progress) => {
    const moduleConfig = config.modules[moduleKey];
    if (!moduleConfig) return null;

    const isLocked = isModuleLocked(moduleKey);

    // DD "Review AI Outputs" availability: the modular reportContent doc exposes
    // the dd-prefixed generated/approved flags and an `outputs` map populated by
    // the Cloud Function. Any of these being truthy means there is something to
    // review, so the Review button should appear on the card.
    const ddReportDoc = reportContent?.diagnosticDecision;
    const ddDocsAvailable = Boolean(
      ddReportDoc?.[getReportGeneratedField('diagnosticDecision')]
      || ddReportDoc?.[getReportApprovedField('diagnosticDecision')]
      || (ddReportDoc?.outputs && Object.keys(ddReportDoc.outputs).length > 0),
    );

    // PAD "Review AI Outputs" availability — same derivation as DD, reading the
    // modular postAssessmentDiscussion reportContent doc.
    const padReportDoc = reportContent?.postAssessmentDiscussion;
    const padDocsAvailable = Boolean(
      padReportDoc?.[getReportGeneratedField('postAssessmentDiscussion')]
      || padReportDoc?.[getReportApprovedField('postAssessmentDiscussion')]
      || (padReportDoc?.outputs && Object.keys(padReportDoc.outputs).length > 0),
    );

    const ddiPaths = getDevelopmentalDiagnosticInterviewPaths({
      patientId,
      historyProgress: PAProgress,
      ssiProgress: SSIProgress,
      isSSIFirst: config.modules.developmentalDiagnosticInterview?.isSSIFirst,
    });

    const ddiClinician = patientDataModel?.clinicians?.clinicianCI?.clinicianName
      || patientDataModel?.clinicians?.clinicianSSI?.clinicianName
      || patientDataModel?.clinicians?.clinicianLead?.clinicianName;

    const moduleProps = {
      developmentalDiagnosticInterview: {
        clinician: ddiClinician,
        supportingClinicians: [],
        startPath: ddiPaths.startPath,
        reviewPath: ddiPaths.reviewPath,
        docsAvailable: Boolean(
          patientDataModel?.dataModel?.['2_clinicalInterview']?.docs_available
          || patientDataModel?.dataModel?.['3_semiStructuredInterview']?.docs_available,
        ),
      },
      behaviouralObservation: {
        clinician: patientDataModel?.clinicians?.clinicianOBS?.clinicianName,
        supportingClinicians: [],
        startPath: getAutismOBSStartRoute({ patientId, assessmentStages }),
        reviewPath: getAutismOBSStepRoutes(patientId).review,
        docsAvailable: false,
      },
      diagnosticDecision: {
        clinician: patientDataModel?.clinicians?.clinicianDD?.clinicianName,
        supportingClinicians: patientDataModel?.clinicians?.clinicianDD?.supportingClinicianNames || [],
        // Modular DD routes. Start resumes at the right step; review jumps to the
        // read-only MDT Review entry point.
        startPath: getAutismDDStartRoute({ patientId, assessmentStages, skipDifferentialsDecisions: wf.skipAutismDifferentialsDecisions }),
        reviewPath: getAutismDDStepRoutes(patientId).mdtReview,
        // "Review AI Outputs" availability is derived from the modular
        // reportContent doc (generated flag, approved flag, or any LLM outputs),
        // not the legacy dataModel docs_available flag.
        docsAvailable: ddDocsAvailable,
      },
      postAssessmentDiscussion: {
        clinician: patientDataModel?.clinicians?.clinicianPAD?.clinicianName,
        supportingClinicians: [],
        // Modular PAD routes. Start resumes at the right step (Assessment
        // Outcome, or Next Steps when the Met outcome's goals are done); review
        // jumps to the Assessment Outcome entry point.
        startPath: getAutismPADStartRoute({ patientId, assessmentStages }),
        reviewPath: getAutismPADStepRoutes(patientId).assessmentOutcome,
        // "Review AI Outputs" availability is derived from the modular
        // reportContent doc (generated flag, approved flag, or any LLM outputs).
        docsAvailable: padDocsAvailable,
      },
    };

    const props = moduleProps[moduleKey] || {};

    const leadClinicianId = patientDataModel?.clinicians?.clinicianLead?.clinicianId;
    const hasCliniciansAssigned = !!leadClinicianId;
    const isAdminOrSupport = claims?.userType === 'Support' || claims?.userType === 'Org. Admin';
    const isLeadClinician = hasCliniciansAssigned && user?.uid === leadClinicianId;
    const canAddSupportingClinician = hasCliniciansAssigned
      && wf.isMDTReview
      && (isAdminOrSupport || isLeadClinician);
    // TOC Autism links Observation primary and DD supporting to the same person.
    // Initial assignment can skip the observation clinician:
    //   - DD/MDT Review keeps the purple "+" next to the Lead badge
    //   - Observation (no primary yet) uses the yellow "Assign Clinician" pill
    // Both open the addDDSupporting reassign step (assigns OBS + DD supporting).
    const hasObsClinician = !!patientDataModel?.clinicians?.clinicianOBS?.clinicianId;
    const showAddSupporting = moduleKey === 'diagnosticDecision'
      && canAddSupportingClinician
      && (!props.supportingClinicians || props.supportingClinicians.length === 0);
    // When Observation is enabled the DD supporting clinician also holds the
    // Observation stage, so the label reflects the dual role. When Observation
    // is disabled (e.g. LMP) it is a plain MDT Review supporting clinician.
    const isObservationEnabled = wf?.isObservationEnabled !== false;
    const addSupportingLabel = isObservationEnabled
      ? 'Add Observation / MDT Review Clinician'
      : 'Add MDT review clinician';
    const showAssignObsClinician = isObservationEnabled
      && moduleKey === 'behaviouralObservation'
      && canAddSupportingClinician
      && !hasObsClinician;

    // Same three SLaM/TOC assign-button scenarios as ADHD PatientOverview:
    // unassigned SLaM patients must still see "Assign Clinician" on empty cards
    // (not only the partial SSI-without-Lead case). Plus Autism Observation when
    // Lead exists but the optional observation clinician was skipped.
    const hasAnyClinician = !!(
      patientDataModel?.clinicians?.clinicianSSI?.clinicianId
      || patientDataModel?.clinicians?.clinicianLead?.clinicianId
    );
    const isSlamAdminUnassignedCard = isSlamOrg(organisationName)
      && isAdminOrSupport
      && !props.clinician;
    const isSlamNeedsInitialAssign = isSlamAdminUnassignedCard && !hasAnyClinician;
    const isSlamNeedsLead = isSlamAdminUnassignedCard
      && hasAnyClinician
      && !leadClinicianId;
    // Symmetric case: Lead exists but SSI is still unassigned.  Handles the
    // reverse of the normal SLaM SSI-first flow — when an admin assigns the
    // Lead bundle before assigning the SSI clinician.  Mirrors the identical
    // condition in ADHD PatientOverview.js for consistency.
    const isSlamNeedsSSI = isSlamAdminUnassignedCard
      && hasAnyClinician
      && !!leadClinicianId
      && !patientDataModel?.clinicians?.clinicianSSI?.clinicianId;
    const isNonSlamNeedsClinician = !isSlamOrg(organisationName)
      && isAdminOrSupport
      && !hasAnyClinician
      && !props.clinician;
    const showAssignButton =
      isSlamNeedsInitialAssign
      || isSlamNeedsLead
      || isSlamNeedsSSI
      || isNonSlamNeedsClinician
      || showAssignObsClinician;
    const assignButtonLabel = isSlamNeedsLead
      ? 'Assign Lead'
      : isSlamNeedsSSI
        ? 'Assign SSI'
        : 'Assign Clinician';

    // Dual-button mode (Preview + Start/Continue/Review) for the appointment
    // cards. Every actionable Autism module supports it: anyone with patient
    // access can Preview (read-only), but only the assigned clinician/Org-Admin
    // gets an enabled action button. In-development and locked modules skip this
    // since they have no actionable path.
    const isDualButtonModule = (
      moduleKey === 'developmentalDiagnosticInterview'
      || moduleKey === 'behaviouralObservation'
      || moduleKey === 'diagnosticDecision'
      || moduleKey === 'postAssessmentDiscussion'
    )
      && !shouldDisableModule(moduleKey)
      && !isLocked
      && props.startPath;

    let dualButtonProps = {};
    if (isDualButtonModule) {
      // Observation resolves its own destination: getAutismOBSStartRoute already
      // returns Review and Synthesis once mapping is complete, and Criterion
      // Mapping while it is not. Honouring reviewPath at 100% would override
      // that — which matters for patients whose stored `progress` predates the
      // three-phase fraction and can still read 100% after task notes alone.
      const usesSmartResume = moduleKey === 'behaviouralObservation';
      const actionPath = (progress === 100 && !usesSmartResume)
        ? (props.reviewPath || props.startPath)
        : props.startPath;
      const actionLabel = progress === 0 ? 'Start' : progress === 100 ? 'Review' : 'Continue';

      // Each module's preview opens its own page in read-only mode. DD and PAD
      // preview at their module entry step (MDT Review / Assessment Outcome);
      // from there the preview-aware navigation keeps ?preview=true as the
      // viewer moves through the remaining steps of the same stage.
      const previewBasePaths = {
        developmentalDiagnosticInterview: `/patient/${patientId}/adult-autism/history`,
        behaviouralObservation: getAutismOBSStepRoutes(patientId).taskAdministration,
        diagnosticDecision: getAutismDDStepRoutes(patientId).mdtReview,
        postAssessmentDiscussion: getAutismPADStepRoutes(patientId).assessmentOutcome,
      };
      const previewPath = appendPreviewQuery(previewBasePaths[moduleKey] || props.startPath);
      const canStart = canStartAppointment(patientDataModel, user?.uid, actionPath);

      const actionState = getAppointmentChecklistActionState({
        enabled: checklistEnabled,
        moduleKey,
        actionLabel,
      });

      dualButtonProps = { previewPath, actionPath, actionLabel, canStart, actionState };
    }

    // Appointment-date pill (adult ADHD parity). Shows the clinician-set date,
    // or the London day of the stage's report-content anchor as an inferred
    // fallback, plus the edit affordance. Disabled cards stay dateless exactly
    // as ADHD's disabled cards do. The MDT Review (Diagnostic Decision) is made
    // in the clinician's own time rather than a patient appointment, so its
    // label reads plainly as "Date" — same rationale as ADHD's DD card.
    let appointmentDate = null;
    let appointmentDateInferred = false;
    let onEditAppointmentDate = null;
    if (!shouldDisableModule(moduleKey) && wf.isAppointmentDatesEnabled) {
      const resolved = resolveModuleAppointmentDate(moduleKey);
      appointmentDate = resolved.date;
      appointmentDateInferred = resolved.isInferred;
      onEditAppointmentDate = () => setAppointmentDateDialogModule(resolved.shortKey);
    }
    const appointmentDateLabel = moduleKey === 'diagnosticDecision' ? 'Date' : 'Appointment date';

    return (
      <div className={getAssessmentModuleCardWidthClass(config.moduleOrder.length)} key={moduleKey}>
        <AssessmentModuleCard
          title={moduleConfig.title}
          clinician={props.clinician}
          supportingClinicians={props.supportingClinicians}
          progress={progress}
          patientId={patientId}
          startPath={props.startPath}
          reviewPath={props.reviewPath}
          docsAvailable={props.docsAvailable}
          isLocked={isLocked}
          isDisabled={shouldDisableModule(moduleKey)}
          disabledReason={getDisabledReason(moduleKey)}
          circleNumber={moduleConfig.circleNumber}
          lockedTooltip={getModuleLockedTooltip(moduleKey)}
          showAddSupportingButton={showAddSupporting}
          onAddSupportingClick={() => setRequestReassignStep('addDDSupporting')}
          addSupportingLabel={addSupportingLabel}
          showAssignClinicianButton={showAssignButton}
          onAssignClinicianClick={() => {
            // Observation empty-slot uses the yellow Assign pill but still opens
            // the linked OBS + DD supporting add flow — not the initial Lead assign dialog.
            if (showAssignObsClinician) {
              setRequestReassignStep('addDDSupporting');
              return;
            }
            setShowAssignLeadDialog(true);
          }}
          assignClinicianLabel={assignButtonLabel}
          appointmentDate={appointmentDate}
          appointmentDateInferred={appointmentDateInferred}
          onEditAppointmentDate={onEditAppointmentDate}
          appointmentDateLabel={appointmentDateLabel}
          {...dualButtonProps}
        />
      </div>
    );
  };

  const breadcrumbTuples = [
    ['Patient Overview', `/patient/${patientId}/adult-autism`],
  ];

  const progressMap = {
    developmentalDiagnosticInterview: ddiProgress,
    behaviouralObservation: behaviouralObservationProgress,
    diagnosticDecision: DDProgress,
    postAssessmentDiscussion: PADProgress,
  };

  if (!patientDataModel) {
    return <div>Loading...</div>;
  }

  return (
    <Layout activePage="dashboard">
      <div className="flex items-center justify-between mb-4 animate-fadeIn">
        <Breadcrumb tupleList={breadcrumbTuples} />
      </div>

      <div className="flex-grow pl-8 pr-8 animate-fadeIn">
        <PatientInfoPanel
          patientDataModel={patientDataModel}
          patientId={patientId}
          informantData={informantData}
          refreshPatientData={() => fetchPatientData(patientId)}
          isSubclinicalService={isSubclinicalService}
          requestReassignStep={requestReassignStep}
          onReassignStepHandled={() => setRequestReassignStep(null)}
          documentsRoute={`/patient/${patientId}/adult-autism/documents`}
          onManageAppointmentDates={
            wf.isAppointmentDatesEnabled
              ? () => setAppointmentDateDialogModule('all')
              : undefined
          }
        />

        {!isSubclinicalService && (
          <>
            <div className="text-gray-900 text-xl font-semibold leading-7 text-left pt-4 pb-4">
              Appointments
            </div>

            <div className="w-full justify-between items-stretch gap-1 inline-flex">
              {config.moduleOrder.map((moduleKey) => (
                renderModuleCard(moduleKey, progressMap[moduleKey] || 0)
              ))}
            </div>

          </>
        )}

        {showAssignLeadDialog && (
          <AssignCliniciansDialog
            key={`assign-lead-${patientId}`}
            isOpen={showAssignLeadDialog}
            onClose={() => {
              setShowAssignLeadDialog(false);
              fetchPatientData(patientId);
            }}
            patientId={patientId}
            patientDataModel={patientDataModel}
            refreshPatientData={() => fetchPatientData(patientId)}
          />
        )}

        {/* Manage Appointment Dates — the shared dialog with the Autism
            re-finalisation strategy injected. moduleOrder/modules come from
            the org config, so Observation is automatically absent for orgs
            that skip it (LMP); editableModules mirrors the same list so no
            hidden module can be scheduled. manageReports is left empty: the
            ADHD default predictor is replaced by predictAutismRefinalisation,
            which reads the modular reportContent approval flags instead. */}
        {wf.isAppointmentDatesEnabled && (
          <AppointmentDateDialog
            isOpen={appointmentDateDialogModule !== null}
            onClose={() => setAppointmentDateDialogModule(null)}
            patientId={patientId}
            appointmentDates={patientDataModel?.appointmentDates || {}}
            inferredDates={Object.fromEntries(
              config.moduleOrder.map((moduleKey) => {
                const resolved = resolveModuleAppointmentDate(moduleKey);
                return [resolved.shortKey, resolved.isInferred ? resolved.date : null];
              }),
            )}
            moduleOrder={config.moduleOrder}
            modules={config.modules}
            refreshPatientData={() => fetchPatientData(patientId)}
            initialModule={
              appointmentDateDialogModule && appointmentDateDialogModule !== 'all'
                ? appointmentDateDialogModule
                : null
            }
            editableModules={config.moduleOrder
              .map((moduleKey) => getAutismAppointmentShortKey(moduleKey))
              .filter(Boolean)}
            organisation={patientDataModel?.organisation}
            manageReports={{}}
            documentsRoute={`/patient/${patientId}/adult-autism/documents`}
            predictRefinalisation={predictAutismRefinalisation}
            rerenderReports={rerenderAfterAutismAppointmentDateChange}
          />
        )}

        <div className="text-gray-900 text-xl font-semibold leading-7 text-left pt-4 pb-4">
          Pre-Assessment Information
        </div>

        {patientDataModel.dataModel['1_preAppointmentInformation']?.status === 'noData' && (
          <StatusMessage
            color="gray"
            icon={<InformationCircleIcon className="w-5 h-5 mr-2" />}
          >
            Information will become available here as the patient completes their pre-assessment questionnaires.
          </StatusMessage>
        )}

        {patientDataModel.dataModel['1_preAppointmentInformation']?.status === 'withData' && (
          <AutismPreAppointmentDashboard
            key={patientId}
            patientDataModel={patientDataModel}
            informantData={informantData}
            patientId={patientId}
            refreshPatientData={() => fetchPatientData(patientId)}
          />
        )}
      </div>
    </Layout>
  );
}

export default AutismPatientOverview;
